import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getStorageUrl } from '../../config/api';

/**
 * Reusable Avatar component with fallback to initials, story ring, and online status
 *
 * @param {Object} props
 * @param {Object} props.user - User object containing avatar, display_name, name, username, first_name, last_name
 * @param {string} props.src - Direct image source (alternative to user object)
 * @param {string} props.alt - Alt text for the image
 * @param {string} props.size - Size preset: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl'
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.border - Show border around avatar
 * @param {string} props.borderColor - Border color class (e.g., 'border-white/30')
 * @param {boolean} props.online - Show online indicator
 * @param {boolean} props.showOnlineStatus - Whether to show online status (respects user settings)
 * @param {boolean} props.hasStory - User has active stories
 * @param {boolean} props.hasUnviewedStory - User has stories the current user hasn't seen
 * @param {function} props.onStoryClick - Click handler for story ring (opens story viewer)
 * @param {function} props.onClick - Click handler for avatar itself
 */
const Avatar = ({
  user,
  src,
  alt,
  size = 'md',
  className = '',
  border = false,
  borderColor = 'border-gray-200',
  online = false,
  showOnlineStatus = true,
  hasStory = false,
  hasUnviewedStory = false,
  onStoryClick,
  onClick,
}) => {
  const { t } = useTranslation('common');
  const [imageError, setImageError] = useState(false);

  // Size presets (width/height in pixels, text size, online indicator size, ring padding)
  const sizePresets = {
    xs: { dimension: 'w-6 h-6', text: 'text-[10px]', online: 'w-2 h-2', ringPadding: 'p-[1px]', ringSize: 'w-7 h-7' },
    sm: { dimension: 'w-8 h-8', text: 'text-xs', online: 'w-2.5 h-2.5', ringPadding: 'p-[2px]', ringSize: 'w-9 h-9' },
    md: { dimension: 'w-10 h-10', text: 'text-sm', online: 'w-3 h-3', ringPadding: 'p-[2px]', ringSize: 'w-11 h-11' },
    lg: { dimension: 'w-12 h-12', text: 'text-base', online: 'w-3.5 h-3.5', ringPadding: 'p-[2px]', ringSize: 'w-[52px] h-[52px]' },
    xl: { dimension: 'w-14 h-14', text: 'text-lg', online: 'w-4 h-4', ringPadding: 'p-[2px]', ringSize: 'w-[60px] h-[60px]' },
    '2xl': { dimension: 'w-20 h-20', text: 'text-xl', online: 'w-5 h-5', ringPadding: 'p-[3px]', ringSize: 'w-[86px] h-[86px]' },
    '3xl': { dimension: 'w-24 h-24', text: 'text-2xl', online: 'w-6 h-6', ringPadding: 'p-[3px]', ringSize: 'w-[102px] h-[102px]' },
    '4xl': { dimension: 'w-32 h-32', text: 'text-3xl', online: 'w-7 h-7', ringPadding: 'p-[4px]', ringSize: 'w-[136px] h-[136px]' },
  };

  // Check if className contains width/height overrides (w-full, h-full, etc.)
  const hasCustomSize = className && (
    className.includes('w-full') ||
    className.includes('h-full') ||
    /w-\[.*\]/.test(className) ||
    /h-\[.*\]/.test(className)
  );

  // Get size config - skip dimension if custom size is provided
  const sizeConfig = typeof size === 'string' && sizePresets[size]
    ? sizePresets[size]
    : sizePresets.md;

  // Use empty dimension if custom size is provided via className
  const dimensionClass = hasCustomSize ? '' : sizeConfig.dimension;

  // Get avatar URL from user object or direct src
  const getAvatarUrl = () => {
    if (src) return src.startsWith('http') ? src : getStorageUrl(src);
    // Prefer pre-computed avatar_url (handles multi-cloud storage correctly)
    if (user?.avatar_url) return user.avatar_url;
    if (user?.avatar) return getStorageUrl(user.avatar, user.avatar_disk || 'public');
    if (user?.profile_photo_url) return user.profile_photo_url;
    if (user?.profile_photo) return getStorageUrl(user.profile_photo, user.avatar_disk || 'public');
    return null;
  };

  // Get initials from user object
  // Never use first_name/last_name (private info). Use display_name or username only.
  const getInitials = () => {
    if (!user) return '??';

    // Try display_name
    if (user.display_name) {
      const parts = user.display_name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      return user.display_name.slice(0, 2).toUpperCase();
    }

    // Try username
    if (user.username) {
      return user.username.slice(0, 2).toUpperCase();
    }

    return '??';
  };

  const avatarUrl = getAvatarUrl();
  const initials = getInitials();
  const borderClass = border ? `border-2 ${borderColor}` : '';

  const handleImageError = () => {
    setImageError(true);
  };

  // Reset error state when avatar URL changes
  React.useEffect(() => {
    setImageError(false);
  }, [avatarUrl]);

  // Handle click on the component
  const handleClick = (e) => {
    if (hasStory && onStoryClick) {
      e.stopPropagation();
      onStoryClick(user);
    } else if (onClick) {
      onClick(e);
    }
  };

  // Avatar content (image or initials)
  const avatarContent = avatarUrl && !imageError ? (
    <img
      src={avatarUrl}
      alt={alt || user?.username || user?.display_name || t('userAvatar', 'User avatar')}
      className={`
        ${hasCustomSize ? '' : 'w-full h-full'}
        rounded-full
        object-cover
        flex-shrink-0
        ${borderClass}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
      onError={handleImageError}
    />
  ) : (
    <div
      className={`
        ${hasCustomSize ? '' : 'w-full h-full'}
        rounded-full
        flex-shrink-0
        ${borderClass}
        bg-gradient-to-br from-[var(--color-primary-400)] to-[var(--color-primary-600)]
        flex items-center justify-center
        text-white font-semibold
        ${sizeConfig.text}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
    >
      {initials}
    </div>
  );

  // Determine if we should show story ring
  const showStoryRing = hasStory && !hasCustomSize;

  // Ring gradient based on viewed status
  const ringGradient = hasUnviewedStory
    ? 'bg-gradient-to-tr from-[var(--color-primary-500)] via-red-500 to-yellow-500'
    : 'bg-gray-300';

  // Wrapper classes
  const getWrapperClasses = () => {
    if (hasCustomSize) {
      return `relative ${className}`.trim();
    }
    return 'relative inline-block';
  };

  // If story ring is shown, wrap avatar in ring
  if (showStoryRing) {
    return (
      <div className={getWrapperClasses()}>
        <button
          onClick={handleClick}
          className={`
            ${sizeConfig.ringSize}
            ${ringGradient}
            ${sizeConfig.ringPadding}
            rounded-full
            cursor-pointer
            transition-transform hover:scale-105
          `.trim().replace(/\s+/g, ' ')}
          type="button"
          aria-label={hasUnviewedStory ? 'View new story' : 'View story'}
        >
          <div className="w-full h-full rounded-full bg-white p-[2px]">
            <div className="w-full h-full rounded-full overflow-hidden">
              {avatarContent}
            </div>
          </div>
        </button>

        {/* Online indicator */}
        {online && showOnlineStatus && (
          <div
            className={`
              absolute bottom-0 right-0
              ${sizeConfig.online}
              bg-green-500
              rounded-full
              border-2 border-white
              z-10
            `.trim().replace(/\s+/g, ' ')}
          />
        )}
      </div>
    );
  }

  // Regular avatar without story ring
  return (
    <div
      className={getWrapperClasses()}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className={`${hasCustomSize ? 'w-full h-full' : dimensionClass} rounded-full overflow-hidden`}>
        {avatarContent}
      </div>

      {/* Online indicator */}
      {online && showOnlineStatus && (
        <div
          className={`
            absolute bottom-0 right-0
            ${sizeConfig.online}
            bg-green-500
            rounded-full
            border-2 border-white
          `.trim().replace(/\s+/g, ' ')}
        />
      )}
    </div>
  );
};

export default Avatar;
